import type { UserHouseholdData } from 'src/types/Certification';

/**
 * Extracts a human-readable message from an axios error response.
 *
 * The shared axios interceptor flattens error responses into
 * `{ ...response.data, status }`, so the payload may live directly on the
 * thrown object (e.g. `{ detail, error_code }`) OR nested under
 * `response.data`. Both shapes are handled.
 *
 * Also handles `{ error }` and Django field errors ({ field: string[] }).
 */
export function extractApiError(err: unknown, fallback: string): string {
  const errorRecord = (
    typeof err === 'object' && err !== null ? err : {}
  ) as Record<string, unknown>;

  const nested = errorRecord.response as
    | { data?: Record<string, unknown> }
    | undefined;
  const data = nested?.data ?? errorRecord;

  if (!data || typeof data !== 'object') {
    return fallback;
  }
  if (typeof data.error === 'string') {
    return data.error;
  }
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  const firstVal = Object.values(data)[0];
  if (Array.isArray(firstVal) && firstVal.length > 0) {
    return String(firstVal[0]);
  }
  return fallback;
}

/**
 * Returns true if the WS payload's affected_user_id matches the currently
 * selected user, or if the payload carries no affected_user_id (not scoped).
 */
export function isAccountHubEventForUser(
  data: unknown,
  currentUserId: number | null | undefined
): boolean {
  if (!data || typeof data !== 'object') {
    return true;
  }
  const affectedId = (data as Record<string, unknown>).affected_user_id;
  if (affectedId === undefined || affectedId === null) {
    return true;
  }
  return Number(affectedId) === currentUserId;
}

/** Full `contact_info_updated` snapshot payload emitted by the backend. */
export interface FullContactSnapshot {
  affected_user_id: number;
  emails: unknown[];
  phones: unknown[];
}

/**
 * Guards the `contact_info_updated` WS payload. The emitter
 * (`_broadcast_contact_info`) always sends BOTH contact arrays as a complete
 * snapshot, and consumers replace their local arrays wholesale. Requiring both
 * arrays here prevents a future partial payload from silently clearing a
 * consumer's contacts.
 */
export function isFullContactSnapshot(
  data: unknown
): data is FullContactSnapshot {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const d = data as Record<string, unknown>;
  return (
    typeof d.affected_user_id === 'number' &&
    Array.isArray(d.emails) &&
    Array.isArray(d.phones)
  );
}

/**
 * Returns true if the logged-in user holds a managing role
 * (hoh, co_hoh, spouse, or helper) in any of their UserHouseholds.
 * my_role is already scoped to the requesting user by the backend.
 */
export function canManageHousehold(
  userHouseholds: UserHouseholdData[] | null | undefined
): boolean {
  return (userHouseholds ?? []).some(
    (uh) =>
      uh.my_role === 'hoh' ||
      uh.my_role === 'co_hoh' ||
      uh.my_role === 'spouse' ||
      uh.my_role === 'helper'
  );
}

/**
 * Returns true if the target user is a member of any UserHousehold
 * that the current user also belongs to.
 * userHouseholds is already scoped to the requesting user by the backend
 * so no currentUserId parameter is needed.
 */
export function isInSameHousehold(
  userHouseholds: UserHouseholdData[] | null | undefined,
  targetUserId: number
): boolean {
  return (userHouseholds ?? []).some((uh) =>
    uh.members.some((m) => m.user_id === targetUserId)
  );
}

/**
 * Triggers a browser download of backup codes as a plain-text file.
 */
export function downloadCodes(codes: string[]): void {
  const content = [
    'Pronto Housing — Backup Codes',
    'Keep these codes somewhere safe. Each can only be used once.',
    '',
    ...codes,
  ].join('\n');
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'pronto-backup-codes.txt';
  a.click();
  URL.revokeObjectURL(url);
}
